import {
  forwardRef,
  useId,
  useState,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export type AuthTextFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'type'
> & {
  /** Input type. Password fields can also use the visibility toggle. */
  type?: 'text' | 'email' | 'password' | 'tel' | 'search';
  /** Current value — drives whether the clear control is shown. */
  value?: string;
  /** Empty the field. Required so parents (RHF `setValue` / local state) stay in sync. */
  onClear: () => void;
  /** Accessible label for the clear button. */
  clearLabel?: string;
  /** Show an eye toggle for password fields (to the right of Clear). */
  passwordToggle?: boolean;
  isPasswordVisible?: boolean;
  onTogglePasswordVisibility?: () => void;
  /** Maps to `aria-invalid` + error border. */
  isInvalid?: boolean;
  /** Extra classes on the outer relative wrapper. */
  className?: string;
  /** Extra classes on the `<input>`. */
  inputClassName?: string;
  /** Optional trailing control (e.g. custom adornment) after Clear / eye. */
  endAdornment?: ReactNode;
};

// Matches `components/ui/input.tsx` (border-input, bg-background, ring-1
// focus) at the taller auth rhythm, so a login field and a product field
// are recognisably the same control.
const AUTH_INPUT_BASE =
  'w-full rounded-md border bg-background px-4 py-2.5 text-foreground placeholder:text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

const ADORNMENT_BTN =
  'rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';

/**
 * Auth text field with a clear (X) control — and optional password visibility
 * toggle — for pre-login forms. Clear appears only while focused and non-empty,
 * so idle fields stay uncluttered.
 */
export const AuthTextField = forwardRef<HTMLInputElement, AuthTextFieldProps>(
  function AuthTextField(
    {
      type = 'text',
      value,
      onClear,
      clearLabel = 'Clear',
      passwordToggle = false,
      isPasswordVisible = false,
      onTogglePasswordVisibility,
      isInvalid = false,
      disabled,
      className,
      inputClassName,
      endAdornment,
      id: idProp,
      onFocus,
      onBlur,
      ...props
    },
    ref,
  ) {
    const autoId = useId();
    const id = idProp ?? autoId;
    const [isFocused, setIsFocused] = useState(false);
    const hasValue = typeof value === 'string' ? value.length > 0 : Boolean(value);
    // Focus-gated: avoid a permanent X on every filled field at rest.
    const showClear = isFocused && hasValue && !disabled;
    const showEye = passwordToggle && typeof onTogglePasswordVisibility === 'function';
    // Reserve Clear slot while focused (even if empty) so the caret doesn't jump
    // when the first character makes the X appear.
    const clearSlot = isFocused && !disabled ? 1 : 0;
    const trailingCount = clearSlot + (showEye ? 1 : 0) + (endAdornment ? 1 : 0);
    const resolvedType =
      passwordToggle ? (isPasswordVisible ? 'text' : 'password') : type;

    function handleFocus(event: FocusEvent<HTMLInputElement>) {
      setIsFocused(true);
      onFocus?.(event);
    }

    function handleBlur(event: FocusEvent<HTMLInputElement>) {
      setIsFocused(false);
      onBlur?.(event);
    }

    return (
      <div className={cn('relative', className)}>
        <input
          {...props}
          ref={ref}
          id={id}
          type={resolvedType}
          value={value}
          disabled={disabled}
          aria-invalid={isInvalid || undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(
            AUTH_INPUT_BASE,
            isInvalid ? 'border-destructive' : 'border-input',
            trailingCount >= 2 && 'pr-20',
            trailingCount === 1 && 'pr-11',
            inputClassName,
          )}
        />
        {(showClear || showEye || endAdornment) ? (
          <div className="absolute inset-y-0 right-2 flex items-center justify-end gap-0.5">
            {showClear ? (
              <button
                type="button"
                onClick={onClear}
                disabled={disabled}
                // Keep focus on the input so Clear stays usable (blur would hide it).
                onMouseDown={(event) => event.preventDefault()}
                className={cn(ADORNMENT_BTN, 'p-1.5')}
                aria-label={clearLabel}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : null}
            {showEye ? (
              <button
                type="button"
                onClick={onTogglePasswordVisibility}
                disabled={disabled}
                onMouseDown={(event) => event.preventDefault()}
                className={cn(ADORNMENT_BTN, 'p-1.5')}
                aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
              >
                {isPasswordVisible ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            ) : null}
            {endAdornment}
          </div>
        ) : null}
      </div>
    );
  },
);
